import React, { useEffect } from 'react';
import { Navigate } from 'react-router';
import { useRouterProps } from '../../utils/withRouter';
import { lazyWithPreload } from '../../utils/lazyLoading';
import { reportRoutePageview } from '../../utils/analytics';
import safeStorage from '../../utils/safeStorage';
import { isLoggedIn } from '../../utils/session';

const NotificationModel = lazyWithPreload(() => import('./notificationModel'));
const Header = lazyWithPreload(() => import('../../components/Header/index'));

// MODULE SCOPE IS LOAD-BEARING. AuthorizedShell owns both the lazy chrome and the pageview effect,
// and declaring it inside the <Route render> prop below would break both silently: a component
// declared there is a NEW component type on every render, so React tears the chrome down, remounts
// it and re-runs this effect on EVERY render — navigation or not. (Hooks inside such a component are
// legal and do run; it is the fresh identity per render, not the hook, that is the defect.)
const AuthorizedShell = ({ component: Component, translator, ...props }) => {
  const { location } = props;
  useEffect(() => {
    // One page view per navigation, read from the ROUTER location, never from window.location
    // (PVR-2). history v4 mints a fresh key for every push/replace, so the key is the identity of
    // the navigation: a plain re-render keeps it and must not re-report.
    reportRoutePageview(location.pathname + location.search);
    // location.key is the whole navigation identity; depending on `location` itself would re-report
    // on every new location object, which is what this effect exists to stop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  return (
    <><NotificationModel className="fullscreen-notification-modal" translator={translator}/><Header/><Component {...props} /></>
  );
}

// `name` (the route table passes one, as react-router 5's did) is not handed on: the page got only
// the router props before, and still does.
const PrivateRoute = ({ component: Component }) => {
  // Guard the parse: a malformed or empty `translated-text` makes JSON.parse throw, and this
  // sits ABOVE the auth check, so a corrupt value crashed the whole route — including the
  // login redirect for logged-out visitors (PVR-1). Fall back to null on bad JSON.
  let translator = null;
  try {
    translator = JSON.parse(safeStorage.getItem('translated-text'));
  } catch (e) {
    translator = null;
  }
  // A route element since react-router 8 (PANEL-1549): the route table matches the path, and the
  // page gets react-router 5's history/location/match from utils/withRouter, as the <Route render>
  // props this replaced handed it.
  const routerProps = useRouterProps();
  if (isLoggedIn()) {
    // Below the auth check on purpose: an anonymous visitor to a protected path is redirected
    // and reported nowhere (PVR-7).
    return <AuthorizedShell {...routerProps} component={Component} translator={translator} />;
  }
  return <Navigate to="/" replace state={{ from: routerProps.location }} />;
}

export default PrivateRoute
